import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import {
  Users, Bus, Ticket, Package, TrendingUp, MapPin, Calendar,
  ChevronRight, DollarSign, BarChart2, Trophy, Clock, AlertTriangle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const statusLabel = {
  agendada: 'Agendada', em_andamento: 'Em Andamento', finalizada: 'Finalizada', cancelada: 'Cancelada',
  recebida: 'Recebida', em_transito: 'Em Trânsito', entregue: 'Entregue'
};
const statusColor = {
  agendada: '#3B82F6', em_andamento: '#F59E0B', finalizada: '#10B981', cancelada: '#EF4444',
  recebida: '#3B82F6', em_transito: '#F59E0B', entregue: '#10B981'
};
const statusBadge = (s) => ({
  agendada: 'bg-blue-100 text-blue-700',
  em_andamento: 'bg-amber-100 text-amber-700',
  finalizada: 'bg-emerald-100 text-emerald-700',
  cancelada: 'bg-red-100 text-red-700'
}[s] || 'bg-slate-100 text-slate-700');

const fmt = (v) => v?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFinanceiro = ['admin', 'financeiro'].includes(user?.role);

  useEffect(() => {
    api.get('/dashboard/stats')
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const kpiCards = stats ? [
    { label: 'Total de Clientes', value: stats.total_clientes, icon: Users, accent: '#0147a0', iconBg: '#EEF2FF', iconColor: '#0147a0', link: '/clientes' },
    { label: 'Total de Viagens', value: stats.total_viagens, icon: Bus, accent: '#d72d21', iconBg: '#FEE2E2', iconColor: '#d72d21', link: '/viagens' },
    { label: 'Reservas Ativas', value: stats.total_reservas, icon: Ticket, accent: '#339933', iconBg: '#DCFCE7', iconColor: '#339933', link: '/reservas' },
    { label: 'Encomendas', value: stats.total_encomendas, icon: Package, accent: '#c99a1a', iconBg: '#FFFBEB', iconColor: '#c99a1a', link: '/encomendas' },
  ] : [];

  const finCards = isFinanceiro && stats ? [
    {
      label: 'Receita Total',
      value: `R$ ${fmt(stats.receita_total)}`,
      icon: DollarSign,
      color: 'bg-emerald-50 text-emerald-700',
      accent: '#339933',
      sub: `${stats.reservas_com_valor} reservas com valor`
    },
    {
      label: 'Ticket Médio',
      value: `R$ ${fmt(stats.ticket_medio)}`,
      icon: BarChart2,
      color: 'bg-blue-50 text-blue-700',
      accent: '#0147a0',
      sub: 'por reserva confirmada'
    },
    {
      label: 'Contas a Vencer',
      value: `R$ ${fmt(stats.contas_a_vencer_total)}`,
      icon: Clock,
      color: 'bg-amber-50 text-amber-700',
      accent: '#F59E0B',
      sub: `${stats.contas_a_vencer_count} nos próximos 7 dias`
    },
    {
      label: 'Contas Vencidas',
      value: `R$ ${fmt(stats.contas_vencidas_total)}`,
      icon: AlertTriangle,
      color: 'bg-red-50 text-red-700',
      accent: '#EF4444',
      sub: `${stats.contas_vencidas_count} conta(s) vencida(s)`
    },
  ] : [];

  const viagensChartData = stats
    ? Object.entries(stats.viagens_status).map(([k, v]) => ({ name: statusLabel[k] || k, value: v, color: statusColor[k] }))
    : [];

  const encomendaChartData = stats
    ? Object.entries(stats.encomendas_status).map(([k, v]) => ({ name: statusLabel[k] || k, value: v, color: statusColor[k] }))
    : [];

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className="space-y-8 page-enter" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0f172a' }}>
            {greeting}, {user?.nome?.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 text-sm mt-1">Resumo operacional — Gugu Turismo</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400 bg-white rounded-xl px-3 py-2 shadow-sm border border-slate-100">
          <Calendar className="w-3.5 h-3.5" />
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
        </div>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 animate-pulse shadow-sm">
              <div className="h-4 bg-slate-100 rounded-lg w-3/4 mb-4" />
              <div className="h-9 bg-slate-100 rounded-lg w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {kpiCards.map((card) => (
            <Link key={card.label} to={card.link}
              data-testid={`kpi-${card.label.replace(/\s+/g,'-').toLowerCase()}`}
              className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 block group">
              {/* Accent bar */}
              <div className="h-1" style={{ background: card.accent }} />
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{card.label}</p>
                    <p className="text-4xl font-bold mt-2 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0f172a' }}>
                      {card.value}
                    </p>
                  </div>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: card.iconBg }}>
                    <card.icon className="w-5 h-5" style={{ color: card.iconColor }} />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-4 text-xs font-medium" style={{ color: card.accent }}>
                  <TrendingUp className="w-3 h-3" />
                  <span>Ver detalhes</span>
                  <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Financial Cards */}
      {isFinanceiro && !loading && finCards.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Resumo Financeiro</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {finCards.map((card) => (
              <div key={card.label} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm"
                data-testid={`fin-${card.label.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="h-1" style={{ background: card.accent }} />
                <div className="p-6 flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{card.label}</p>
                    <p className="text-2xl font-bold mt-2 text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{card.value}</p>
                    <p className="text-xs text-slate-400 mt-1">{card.sub}</p>
                  </div>
                  <div className={`w-11 h-11 rounded-xl ${card.color} flex items-center justify-center flex-shrink-0`}>
                    <card.icon className="w-5 h-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Próximas Viagens */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Próximas Viagens
            </h2>
            <Link to="/viagens" className="text-xs font-bold uppercase tracking-wider hover:underline"
              style={{ color: '#0147a0' }}>Ver todas</Link>
          </div>
          {!stats?.proximas_viagens?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-300">
              <Bus className="w-10 h-10 mb-2" />
              <p className="text-sm">Nenhuma viagem agendada</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {stats.proximas_viagens.map((v) => (
                <div key={v.id} className="px-6 py-4 hover:bg-[#F0F4FF]/60 transition-colors"
                  data-testid={`proxima-viagem-${v.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{v.titulo}</p>
                      <div className="flex items-center gap-2 mt-1 text-slate-500 text-xs">
                        <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: '#d72d21' }} />
                        <span>{v.origem} → {v.destino}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-slate-400 text-xs">
                        <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: '#0147a0' }} />
                        <span>{new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')} às {v.horario}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusBadge(v.status)}`}>
                        {statusLabel[v.status]}
                      </span>
                      <span className="text-xs text-slate-400">{v.assentos_ocupados}/{v.capacidade}</span>
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{
                          width: `${v.capacidade > 0 ? (v.assentos_ocupados/v.capacidade)*100 : 0}%`,
                          background: '#0147a0'
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {isFinanceiro && stats?.top_viagens_receita?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4" style={{ color: '#f9c900' }} />
                <h2 className="font-bold text-slate-800 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Top Receita
                </h2>
              </div>
              <div className="space-y-2">
                {stats.top_viagens_receita.map((tv, i) => (
                  <div key={tv.viagem_id || `top-${i}`} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                        style={{ background: i === 0 ? '#f9c900' : i === 1 ? '#94a3b8' : '#cd7f32', color: i === 0 ? '#000' : '#fff' }}>
                        {i+1}
                      </span>
                      <p className="text-xs font-semibold text-slate-700 truncate">{tv.titulo}</p>
                    </div>
                    <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: '#339933' }}>
                      R$ {fmt(tv.receita)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status viagens */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h2 className="font-bold text-slate-800 text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Status das Viagens
            </h2>
            {viagensChartData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={viagensChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#94A3B8' }} />
                  <YAxis tick={{ fontSize: 9, fill: '#94A3B8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[4,4,0,0]}>
                    {viagensChartData.map((e, i) => <Cell key={e.name || `v-${i}`} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-300 text-xs text-center py-6">Sem dados</p>}
          </div>

          {/* Status encomendas */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h2 className="font-bold text-slate-800 text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Status das Encomendas
            </h2>
            {encomendaChartData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={encomendaChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#94A3B8' }} />
                  <YAxis tick={{ fontSize: 9, fill: '#94A3B8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[4,4,0,0]}>
                    {encomendaChartData.map((e, i) => <Cell key={e.name || `enc-${i}`} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-300 text-xs text-center py-6">Sem dados</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
